/**
 * PHASE 8: OfflineToastBridge Component
 * Bridge between offline sync events and toast notifications
 * 
 * Automatically shows toasts for:
 * - Going offline/online
 * - Sync completion
 * - Sync failures
 * - Conflict detection
 */

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { 
  WifiOff, 
  Wifi, 
  CheckCircle, 
  AlertCircle, 
  AlertTriangle,
  RefreshCw,
  CloudOff
} from 'lucide-react';
import { useOfflineSync } from '@/hooks/useOfflineSync';

/**
 * Toast configurations for different events
 */
const TOAST_CONFIG = {
  offline: {
    title: 'You are offline',
    description: 'Your changes will be saved locally and synced when you reconnect.',
    icon: <WifiOff className="h-5 w-5 text-amber-500" />,
    duration: 5000
  },
  online: {
    title: 'Back online!',
    description: 'Syncing your changes...',
    icon: <Wifi className="h-5 w-5 text-green-500" />,
    duration: 3000
  },
  syncComplete: {
    title: 'Sync complete',
    description: 'All your changes have been synced.',
    icon: <CheckCircle className="h-5 w-5 text-green-500" />,
    duration: 3000
  },
  syncPartial: {
    title: 'Sync partially complete',
    description: (failed) => `${failed} item${failed !== 1 ? 's' : ''} failed to sync.`,
    icon: <AlertCircle className="h-5 w-5 text-amber-500" />,
    duration: 5000
  },
  syncFailed: {
    title: 'Sync failed',
    description: 'Unable to sync your changes. Will retry automatically.',
    icon: <AlertCircle className="h-5 w-5 text-red-500" />,
    duration: 5000
  },
  conflict: {
    title: 'Conflict detected',
    description: (count) => `${count} item${count !== 1 ? 's' : ''} need your attention.`,
    icon: <AlertTriangle className="h-5 w-5 text-purple-500" />,
    duration: 8000
  },
  queued: {
    title: 'Saved offline',
    description: 'Your action has been saved and will sync when online.',
    icon: <CloudOff className="h-5 w-5 text-blue-500" />,
    duration: 3000
  }
};

/**
 * OfflineToastBridge - Listens to sync events and shows toasts
 */
export function OfflineToastBridge({ 
  showOfflineNotice = true,
  showOnlineNotice = true,
  showSyncNotice = true,
  showConflictNotice = true,
  showQueuedNotice = true
}) {
  const {
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    conflictCount
  } = useOfflineSync();

  // Track previous states for change detection
  const prevState = useRef({
    isOnline: true,
    isSyncing: false,
    conflictCount: 0,
    failedCount: 0
  });

  // Handle online/offline transitions
  useEffect(() => {
    const wasOnline = prevState.current.isOnline;
    
    if (wasOnline !== isOnline) {
      if (!isOnline && showOfflineNotice) {
        toast(TOAST_CONFIG.offline.title, {
          description: TOAST_CONFIG.offline.description,
          icon: TOAST_CONFIG.offline.icon,
          duration: TOAST_CONFIG.offline.duration,
          id: 'offline-status'
        });
      } else if (isOnline && !wasOnline && showOnlineNotice) {
        toast.dismiss('offline-status');
        toast(TOAST_CONFIG.online.title, {
          description: TOAST_CONFIG.online.description,
          icon: TOAST_CONFIG.online.icon,
          duration: TOAST_CONFIG.online.duration,
          id: 'online-status'
        });
      }
    }
    
    prevState.current.isOnline = isOnline;
  }, [isOnline, showOfflineNotice, showOnlineNotice]);

  // Handle sync completion
  useEffect(() => {
    const wasSyncing = prevState.current.isSyncing;
    
    if (wasSyncing && !isSyncing && showSyncNotice) {
      // Sync just finished
      if (failedCount > 0) {
        toast(TOAST_CONFIG.syncPartial.title, {
          description: TOAST_CONFIG.syncPartial.description(failedCount),
          icon: TOAST_CONFIG.syncPartial.icon,
          duration: TOAST_CONFIG.syncPartial.duration,
          id: 'sync-status'
        });
      } else if (pendingCount === 0) {
        toast.dismiss('online-status');
        toast(TOAST_CONFIG.syncComplete.title, {
          description: TOAST_CONFIG.syncComplete.description,
          icon: TOAST_CONFIG.syncComplete.icon,
          duration: TOAST_CONFIG.syncComplete.duration,
          id: 'sync-status'
        });
      }
    }
    
    prevState.current.isSyncing = isSyncing;
  }, [isSyncing, failedCount, pendingCount, showSyncNotice]);

  // Handle new conflicts
  useEffect(() => {
    const prevConflicts = prevState.current.conflictCount;
    
    if (conflictCount > prevConflicts && showConflictNotice) {
      toast(TOAST_CONFIG.conflict.title, {
        description: TOAST_CONFIG.conflict.description(conflictCount),
        icon: TOAST_CONFIG.conflict.icon,
        duration: TOAST_CONFIG.conflict.duration,
        action: {
          label: 'View',
          onClick: () => {
            // Could emit event to open drawer
            window.dispatchEvent(new CustomEvent('open-sync-drawer'));
          }
        },
        id: 'conflict-notice'
      });
    }
    
    prevState.current.conflictCount = conflictCount;
  }, [conflictCount, showConflictNotice]);

  // Handle new failures
  useEffect(() => {
    const prevFailed = prevState.current.failedCount;
    
    if (failedCount > prevFailed && failedCount > 0) {
      // New failures detected - this is handled by sync completion toast
      prevState.current.failedCount = failedCount;
    }
  }, [failedCount]);

  // This component doesn't render anything
  return null;
}

/**
 * Show a toast when an action is queued offline
 * Call this function directly when queueing an action
 */
export function showQueuedToast(message) {
  toast(TOAST_CONFIG.queued.title, {
    description: message || TOAST_CONFIG.queued.description,
    icon: TOAST_CONFIG.queued.icon,
    duration: TOAST_CONFIG.queued.duration
  });
}

/**
 * Show a custom sync-related toast
 */
export function showSyncToast(type, customMessage) {
  const config = TOAST_CONFIG[type];
  if (!config) return;

  toast(config.title, {
    description: customMessage || config.description,
    icon: config.icon,
    duration: config.duration
  });
}

/**
 * Show success toast for synced action
 */
export function showSyncedActionToast(actionType) {
  const messages = {
    TIMECLOCK_PUNCH: 'Time clock punch synced successfully',
    AVAILABILITY_UPDATE: 'Availability updated successfully',
    SCHEDULE_ACK: 'Schedule acknowledged successfully',
    default: 'Action synced successfully'
  };

  toast.success(messages[actionType] || messages.default, {
    duration: 3000
  });
}

/**
 * Show error toast for failed action
 */
export function showFailedActionToast(actionType, error) {
  const messages = {
    TIMECLOCK_PUNCH: 'Failed to sync time clock punch',
    AVAILABILITY_UPDATE: 'Failed to update availability',
    SCHEDULE_ACK: 'Failed to acknowledge schedule',
    default: 'Failed to sync action'
  };

  toast.error(messages[actionType] || messages.default, {
    description: error || 'Will retry automatically when online.',
    duration: 5000
  });
}

export default OfflineToastBridge;
